import {
  getFirestore, collection, collectionGroup, doc, addDoc, updateDoc, getDoc, getDocs,
  query, orderBy, limit, where, setDoc, deleteDoc,
  serverTimestamp, onSnapshot, runTransaction,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { app } from './firebase';
import { computeCurviness } from '../utils/curviness';
import { reverseCountry, slugify } from '../utils/geocoding';
import { track } from './analytics';
import { bestDisplayName } from '../utils/formatters';
import { getProfile } from './profileService';
import { buildBusinessSnapshot } from './businessIdentityService';

const db      = getFirestore(app);
const storage = getStorage(app);

const TOURS_COLLECTION = 'tours';
const PAGE_SIZE = 20;

// ─── Image upload ─────────────────────────────────────────────────────────────

async function uploadTourImages(uid, files) {
  return Promise.all(
    files.map(async (file) => {
      const storageRef = ref(storage, `tours/${uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    })
  );
}

// ─── Create draft tour ────────────────────────────────────────────────────────
// Returns the new tour ID. Status is 'draft' until the publishing fee is paid.

export async function createTourDraft({ user, tourData, imageFiles = [], publish = false }) {
  const imageUrls = imageFiles.length
    ? await uploadTourImages(user.uid, imageFiles)
    : [];

  const startWp = tourData.waypoints?.[0];

  // Discovery metadata (best-effort, never blocks publishing). Locate by the
  // route start, falling back to the meeting point for route-less tours.
  const curv = computeCurviness(tourData.geometry);
  const locPt = startWp?.lat != null
    ? { lat: startWp.lat, lng: startWp.lng }
    : (tourData.meetingPoint?.lat != null ? { lat: tourData.meetingPoint.lat, lng: tourData.meetingPoint.lng } : null);
  const loc  = locPt ? await reverseCountry(locPt.lat, locPt.lng).catch(() => null) : null;

  // Lightweight verified-business indicator for every tour/lesson card,
  // snapshotted at publish time (same reason userName/userPhoto are
  // denormalized) — so cards need no extra profile fetch, and a business
  // verified later doesn't retroactively badge older listings.
  const profile = await getProfile(user.uid).catch(() => null);
  const creatorBadge = {
    businessVerified: profile?.businessVerifiedStatus === 'verified',
  };

  const docRef = await addDoc(collection(db, TOURS_COLLECTION), {
    userId:            user.uid,
    userName:          bestDisplayName(user),
    userPhoto:         user.photoURL || null,
    // Business card shown on Community cards + the detail page (null unless
    // the owner opted in) — kept in sync by syncBusinessIdentity.
    business:          buildBusinessSnapshot(profile),

    name:              tourData.name,
    description:       tourData.description || '',
    imageUrls,

    curviness:         curv ? Math.round(curv.degPerKm) : null,
    curvinessLevel:    curv ? curv.level : null,
    country:           loc?.country || null,
    countryCode:       loc?.countryCode || null,
    countrySlug:       loc?.country ? slugify(loc.country) : null,
    region:            loc?.region || null,

    waypoints:         tourData.waypoints   || [],
    // GeoJSON coordinates are nested arrays, which Firestore rejects — store as
    // a JSON string and parse on read (see hydrateTour).
    geometry:          tourData.geometry ? JSON.stringify(tourData.geometry) : null,
    distance:          tourData.distance    || null,
    duration:          tourData.duration    || null,
    startLat:          startWp?.lat ?? null,
    startLng:          startWp?.lng ?? null,
    startName:         startWp?.name        || '',
    endName:           tourData.waypoints?.[tourData.waypoints.length - 1]?.name || '',

    startDate:         tourData.startDate,           // Firestore Timestamp or null
    endDate:           tourData.endDate   || null,
    meetingPoint:      tourData.meetingPoint || null, // { lat, lng, name }

    // Lessons don't use a headcount cap — a day's capacity is however many
    // non-overlapping time slots fit in the availability window, enforced by
    // actual time-overlap checks (registerFree / createTourTicketSession),
    // not a per-date participant count.
    maxParticipants:   tourData.category === 'lesson' ? null : (tourData.maxParticipants || null),
    minParticipants:   tourData.category === 'lesson' ? null : (tourData.minParticipants || null),
    skillLevel:        tourData.skillLevel || 'intermediate',

    // ── Tour-product details (Phase 1) ──
    tourCode:          tourData.tourCode || null,
    durationLabel:     tourData.durationLabel || null,
    departTime:        tourData.departTime || null,
    returnTime:        tourData.returnTime || null,
    highlights:        tourData.highlights || [],
    inclusions:        tourData.inclusions || [],
    includedExtra:     tourData.includedExtra || [],
    notIncluded:       tourData.notIncluded || [],
    securityBond:      tourData.securityBond || null,
    routeNote:         tourData.routeNote || '',

    // ── Schedule (Phase 2) ──
    scheduleType:      tourData.scheduleType || 'oneoff',
    runsOn:            tourData.runsOn || [],
    seasonStart:       tourData.seasonStart || null,
    seasonEnd:         tourData.seasonEnd || null,

    registrationType:  tourData.registrationType || 'free',
    ticketPrice:       tourData.ticketPrice       || null,  // USD e.g. 25.00

    // ── Lessons ──
    category:          tourData.category || 'tour',   // 'tour' | 'lesson'
    learnerFriendly:   tourData.category === 'lesson' ? !!tourData.learnerFriendly : false,
    creatorBadge,      // { businessVerified } — every tour/lesson

    // ── Lesson pricing/availability (Phase 4) ──
    // Lessons are private (1 booking/day) and priced per hour instead of a flat
    // ticket — maxParticipants is hard-pinned to 1 above via the caller
    // (TourBuilder), never trusted from anywhere else.
    hourlyRate:        tourData.category === 'lesson' ? (tourData.hourlyRate || null) : null,
    rentBikeAvailable: tourData.category === 'lesson' ? !!tourData.rentBikeAvailable : false,
    bikeHourlyRate:    tourData.category === 'lesson' && tourData.rentBikeAvailable ? (tourData.bikeHourlyRate || null) : null,
    availabilityStart: tourData.category === 'lesson' ? (tourData.availabilityStart || null) : null,
    availabilityEnd:   tourData.category === 'lesson' ? (tourData.availabilityEnd || null) : null,

    status:            publish ? 'published' : 'draft',
    registrationCount: 0,
    publishedAt:       publish ? serverTimestamp() : null,
    createdAt:         serverTimestamp(),
  });

  if (publish) {
    track('tour_published', {
      tour_id: docRef.id,
      registration_type: tourData.registrationType || 'free',
      ticket_price: tourData.ticketPrice || 0,
    });
  }
  return docRef.id;
}

// ─── Fetch tours ────────��─────────────────────────��───────────────────────────

// Geometry is stored as a JSON string (Firestore can't hold nested arrays);
// parse it back to a GeoJSON object on read.
function hydrateTour(id, data) {
  let geometry = data.geometry;
  if (typeof geometry === 'string') {
    try { geometry = JSON.parse(geometry); } catch { geometry = null; }
  }
  return { id, ...data, geometry };
}

export async function fetchPublishedTours(afterDoc = null) {
  let q = query(
    collection(db, TOURS_COLLECTION),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(PAGE_SIZE)
  );
  if (afterDoc) {
    const { startAfter } = await import('firebase/firestore');
    q = query(q, startAfter(afterDoc));
  }
  const snap = await getDocs(q);
  return {
    tours:   snap.docs.map(d => hydrateTour(d.id, d.data())),
    lastDoc: snap.docs[snap.docs.length - 1] || null,
    hasMore: snap.docs.length === PAGE_SIZE,
  };
}

export async function fetchTour(tourId) {
  const snap = await getDoc(doc(db, TOURS_COLLECTION, tourId));
  if (!snap.exists()) return null;
  return hydrateTour(snap.id, snap.data());
}

export function subscribeTour(tourId, callback) {
  return onSnapshot(doc(db, TOURS_COLLECTION, tourId), snap => {
    if (snap.exists()) callback(hydrateTour(snap.id, snap.data()));
  });
}

export async function fetchMyTours(uid) {
  const q = query(
    collection(db, TOURS_COLLECTION),
    where('userId', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => hydrateTour(d.id, d.data()));
}

// A user's published tours (for their public profile).
export async function fetchPublishedToursByUser(uid) {
  const q = query(
    collection(db, TOURS_COLLECTION),
    where('userId', '==', uid),
    where('status', '==', 'published'),
    orderBy('publishedAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => hydrateTour(d.id, d.data()));
}

// Discovery: published tours in a given country (across all organizers).
export async function fetchToursByCountry(slug) {
  const q = query(
    collection(db, TOURS_COLLECTION),
    where('status', '==', 'published'),
    where('countrySlug', '==', slug),
    orderBy('publishedAt', 'desc'),
    limit(100)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => hydrateTour(d.id, d.data()));
}

// Published tours by any of `uids` (chunked: Firestore `in` allows 30 per
// query). Powers the Community "Following" filter.
export async function fetchToursByUsers(uids) {
  if (!uids?.length) return [];
  const chunks = [];
  for (let i = 0; i < uids.length; i += 30) chunks.push(uids.slice(i, i + 30));
  const snaps = await Promise.all(chunks.map(c => getDocs(query(
    collection(db, TOURS_COLLECTION),
    where('status', '==', 'published'),
    where('userId', 'in', c),
    limit(100),
  ))));
  return snaps.flatMap(s => s.docs.map(d => hydrateTour(d.id, d.data())));
}

// Tours the user has a confirmed registration for (across all organizers).
export async function fetchMyRegisteredTours(uid) {
  const q = query(
    collectionGroup(db, 'registrations'),
    where('userId', '==', uid),
    where('status', '==', 'confirmed')
  );
  const snap = await getDocs(q);
  const entries = snap.docs
    .map(d => ({ ref: d.ref.parent.parent, regTourDate: d.data().tourDate || null }))
    .filter(e => e.ref);
  const tours = await Promise.all(entries.map(async ({ ref, regTourDate }) => {
    const t = await getDoc(ref);
    return t.exists() ? { ...hydrateTour(t.id, t.data()), regTourDate } : null;
  }));
  return tours.filter(Boolean);
}

// ─── Update tour (organizer edits after publishing) ──────────────────────────

export async function updateTour({ tourId, tourData, newImageFiles = [], removedImageUrls = [] }) {
  // Upload any new images
  const newUrls = newImageFiles.length
    ? await uploadTourImages(tourData.userId, newImageFiles)
    : [];

  // Build final image list: existing kept ones + new uploads
  const keptUrls  = (tourData.imageUrls || []).filter(u => !removedImageUrls.includes(u));
  const imageUrls = [...keptUrls, ...newUrls];

  const patch = {
    name:            tourData.name,
    description:     tourData.description || '',
    imageUrls,
    startDate:       tourData.startDate,
    endDate:         tourData.endDate || null,
    meetingPoint:    tourData.meetingPoint || null,
    maxParticipants: tourData.maxParticipants || null,
    minParticipants: tourData.minParticipants || null,
    skillLevel:      tourData.skillLevel || 'intermediate',
    tourCode:        tourData.tourCode || null,
    durationLabel:   tourData.durationLabel || null,
    departTime:      tourData.departTime || null,
    returnTime:      tourData.returnTime || null,
    highlights:      tourData.highlights || [],
    inclusions:      tourData.inclusions || [],
    includedExtra:   tourData.includedExtra || [],
    notIncluded:     tourData.notIncluded || [],
    securityBond:    tourData.securityBond || null,
    routeNote:       tourData.routeNote || '',
    scheduleType:    tourData.scheduleType || 'oneoff',
    runsOn:          tourData.runsOn || [],
    seasonStart:     tourData.seasonStart || null,
    seasonEnd:       tourData.seasonEnd || null,
  };
  // Only paid tours carry a price; type itself is locked after publishing.
  if (tourData.ticketPrice != null) patch.ticketPrice = tourData.ticketPrice;

  // Lesson pricing/availability — only sent by the TourBuilder for lessons
  // (category itself is locked and never revisited here).
  if (tourData.hourlyRate !== undefined) {
    patch.hourlyRate        = tourData.hourlyRate;
    patch.rentBikeAvailable = !!tourData.rentBikeAvailable;
    patch.bikeHourlyRate    = tourData.rentBikeAvailable ? tourData.bikeHourlyRate : null;
    patch.availabilityStart = tourData.availabilityStart || null;
    patch.availabilityEnd   = tourData.availabilityEnd || null;
  }

  await updateDoc(doc(db, TOURS_COLLECTION, tourId), patch);
}

// ─── Mark tour published (called after Stripe webhook confirms payment) ───────

export async function markTourPublished(tourId) {
  await updateDoc(doc(db, TOURS_COLLECTION, tourId), {
    status:      'published',
    publishedAt: serverTimestamp(),
  });
}

// ─── Delete/cancel tour ���─────────────────────────────���────────────────────────

export async function cancelTour(tourId) {
  await updateDoc(doc(db, TOURS_COLLECTION, tourId), { status: 'cancelled' });
}

// Permanently remove a tour (owner only). Used for already-cancelled tours.
export async function deleteTour(tourId) {
  await deleteDoc(doc(db, TOURS_COLLECTION, tourId));
}

// ─── Registrations ────────��────────────────────────────���──────────────────────

// Registration doc id. One-off tours: the uid (one booking per rider, ever).
// Recurring tours: uid_YYYY-MM-DD, so a rider can book many occurrences while
// each past ride keeps its own check-in record (feeds reputation + reviews).
export function regDocId(userId, tourDate) {
  return tourDate ? `${userId}_${tourDate}` : userId;
}

// Every registration this rider holds on a tour, newest date first.
export async function fetchMyRegistrations(tourId, userId) {
  if (!userId) return [];
  const snap = await getDocs(
    query(collection(db, TOURS_COLLECTION, tourId, 'registrations'), where('userId', '==', userId))
  );
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.tourDate || '').localeCompare(a.tourDate || ''));
}

// The registration that matters right now: the soonest still-upcoming booking,
// falling back to the most recent past one (so "you rode this" still shows).
export async function getMyRegistration(tourId, userId) {
  const all = (await fetchMyRegistrations(tourId, userId)).filter(r => r.status === 'confirmed');
  if (!all.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = all.filter(r => !r.tourDate || r.tourDate >= today);
  // upcoming is newest-first → take the last (soonest).
  return upcoming.length ? upcoming[upcoming.length - 1] : all[0];
}

// Confirmed lesson bookings for one or more dates — { 'YYYY-MM-DD': [{startTime,
// hours}] }. Goes through a Cloud Function (Admin SDK), not a direct Firestore
// query: a rider can only read their OWN registration docs under
// firestore.rules, and a `list` query that isn't provably scoped to
// request.auth.uid is denied outright for anyone else — including the
// organizer's other riders' bookings, which is exactly what a conflict check
// needs to see. That denial was silently swallowed here before, which is why
// an already-booked slot could still show as open.
const GET_LESSON_BOOKINGS_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getLessonBookings';

export async function fetchLessonBookingsForDates(tourId, dates) {
  const uniq = [...new Set(dates)].filter(Boolean).slice(0, 30); // Firestore 'in' cap
  if (!uniq.length) return {};
  const res = await fetch(GET_LESSON_BOOKINGS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tourId, dates: uniq }),
  });
  if (!res.ok) return {};
  const data = await res.json().catch(() => null);
  return data?.bookings || {};
}

// Single-date convenience wrapper over the same endpoint.
export async function fetchLessonDateBookings(tourId, date) {
  if (!date) return [];
  const byDate = await fetchLessonBookingsForDates(tourId, [date]);
  return byDate[date] || [];
}

const REGISTER_FREE_LESSON_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/registerFreeLesson';

// `lessonBooking` (free lessons only): { hours, startTime } — recorded on the
// registration for the check-in card and "My Rides"/"Organizing" views, same
// as the paid path stamps them from the Stripe session metadata.
//
// Lesson bookings are delegated entirely to a Cloud Function: the overlap
// check needs to read OTHER riders' registrations on that date, which a rider
// can't do directly under firestore.rules (a `list` query not provably scoped
// to request.auth.uid is denied outright, even though a couple of the actual
// matches would individually have been theirs to read) — so the client-side
// version of this check always silently saw zero bookings and let overlapping
// slots through. Non-lesson free tours are unaffected — they cap via the tour
// doc's own dateCounts/maxParticipants, which every rider can already read.
export async function registerFree(tourId, user, tourDate = null, lessonBooking = null) {
  if (lessonBooking) {
    const idToken = await user.getIdToken();
    const res = await fetch(REGISTER_FREE_LESSON_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ tourId, tourDate, hours: lessonBooking.hours, startTime: lessonBooking.startTime }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
    return;
  }

  const tourSnap = await getDoc(doc(db, TOURS_COLLECTION, tourId));
  if (!tourSnap.exists()) throw new Error('Tour not found');
  const tour = tourSnap.data();

  // Single-date tours cap on the global count; recurring tours cap per chosen
  // date via the dateCounts map (maintained server-side by syncRegistrationCount).
  const recurring = tour.scheduleType === 'recurring';
  if (recurring) {
    if (!tourDate) throw new Error('Please choose a date');
    if (tour.maxParticipants) {
      const onDate = tour.dateCounts?.[tourDate] || 0;
      if (onDate >= tour.maxParticipants) throw new Error('That date is full');
    }
  } else if (tour.maxParticipants && tour.registrationCount >= tour.maxParticipants) {
    throw new Error('This tour is full');
  }

  // Recurring tours key by date, so booking another occurrence is a new doc
  // rather than an overwrite of the previous ride.
  const regRef = doc(db, TOURS_COLLECTION, tourId, 'registrations',
    regDocId(user.uid, recurring ? tourDate : null));
  const existing = await getDoc(regRef);
  if (existing.exists() && existing.data().status === 'confirmed') {
    throw new Error(recurring ? 'You’re already booked on that date' : 'Already registered');
  }

  // registrationCount is maintained server-side by the syncRegistrationCount
  // Cloud Function trigger — writing the tour doc here would fail for non-owners.
  await setDoc(regRef, {
    userId:        user.uid,
    userName:      bestDisplayName(user),
    userEmail:     user.email || '',
    userPhoto:     user.photoURL || null,
    status:        'confirmed',
    paymentStatus: 'free',
    tourDate:      tourDate || null,   // chosen occurrence for recurring tours
    checkinToken:  crypto.randomUUID().replace(/-/g, ''),  // QR check-in secret
    checkedIn:     false,
    registeredAt:  serverTimestamp(),
  });
}

// Lazily attach a check-in token to a pre-existing registration that predates
// the QR feature (rider-owned write, allowed by rules). Returns the token.
export async function ensureCheckinToken(tourId, regId) {
  const regRef = doc(db, TOURS_COLLECTION, tourId, 'registrations', regId);
  const snap = await getDoc(regRef);
  if (!snap.exists()) return null;
  const existing = snap.data().checkinToken;
  if (existing) return existing;
  const token = crypto.randomUUID().replace(/-/g, '');
  await updateDoc(regRef, { checkinToken: token, checkedIn: snap.data().checkedIn ?? false });
  return token;
}

// ─── Reviews (riders rate a tour after the ride) ─────────────────────────────

export async function getMyTourReview(tourId, userId) {
  if (!db || !userId) return null;
  const snap = await getDoc(doc(db, TOURS_COLLECTION, tourId, 'reviews', userId));
  return snap.exists() ? snap.data() : null;
}

export async function fetchTourReviews(tourId) {
  const snap = await getDocs(
    query(collection(db, TOURS_COLLECTION, tourId, 'reviews'), orderBy('createdAt', 'desc'), limit(50))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Adds/updates the user's review and keeps the tour's rating aggregate in sync
// transactionally (ratingTotal / ratingCount / averageRating).
export async function submitTourReview(tourId, user, { stars, text }) {
  const tourRef   = doc(db, TOURS_COLLECTION, tourId);
  const reviewRef = doc(db, TOURS_COLLECTION, tourId, 'reviews', user.uid);

  await runTransaction(db, async tx => {
    const [tourSnap, reviewSnap] = await Promise.all([tx.get(tourRef), tx.get(reviewRef)]);
    if (!tourSnap.exists()) throw new Error('Tour no longer exists');

    const { ratingTotal = 0, ratingCount = 0 } = tourSnap.data();
    const wasRated = reviewSnap.exists();
    const oldStars = wasRated ? reviewSnap.data().stars : 0;

    const newTotal = ratingTotal - oldStars + stars;
    const newCount = wasRated ? ratingCount : ratingCount + 1;

    tx.set(reviewRef, {
      stars,
      text:      text || '',
      userId:    user.uid,
      userName:  bestDisplayName(user),
      userPhoto: user.photoURL || null,
      createdAt: serverTimestamp(),
    });
    tx.update(tourRef, {
      ratingTotal:   newTotal,
      ratingCount:   newCount,
      averageRating: newTotal / newCount,
    });
  });
}

// Owner-only safety net: corrects a stale registrationCount to match the actual
// number of confirmed registrations. Used to heal docs that drifted before the
// server-side trigger existed.
export async function reconcileRegistrationCount(tourId, confirmedCount) {
  await updateDoc(doc(db, TOURS_COLLECTION, tourId), {
    registrationCount: confirmedCount,
  });
}

export async function fetchTourRegistrations(tourId) {
  const q = query(
    collection(db, TOURS_COLLECTION, tourId, 'registrations'),
    where('status', '==', 'confirmed'),
    orderBy('registeredAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
